import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, first, map, shareReplay, switchMap } from 'rxjs/operators';

import { APIService, KPI } from '../api';
import { IdNameMixin } from '../api/models';
import { InvestmentExecutionsService } from './investment-executions.service';
import { InvestmentReasonsService } from './investment-reasons.service';
import { idMapToArray, ScopeService } from './scope.service';

import { HttpClient } from '@angular/common/http';

import { environment } from '../../../environments/environment';
import { RoadmapService } from './roadmap.service';

export interface DataToMap {
  name: string;
  type: string;
  clientData: any;
  values: IdNameMixin[];
}

export interface TypeMapping {
  [type: string]: IdNameMixin[];
}

@Injectable()
export class ImportsService {
  private _investmentImportableInvestmentStatuses$: Observable<
    KPI.Display<KPI.ImportableInvestmentStatus>[]
  >;
  private _assetImportableInvestmentStatuses$: Observable<
    KPI.Display<KPI.ImportableAssetStatus>[]
  >;

  private _assetColumnsDefinition$: Observable<KPI.ColumnDefinition[]>;
  private assetColumnsRefresh$ = new BehaviorSubject<void>(null);

  private _investmentColumnsDefinition$: Observable<KPI.ColumnDefinition[]>;
  private investmentColumnsRefresh$ = new BehaviorSubject<void>(null);

  private _assetPictureColumnsDefinition$: Observable<KPI.ColumnDefinition[]>;
  private assetPictureColumnsRefresh$ = new BehaviorSubject<void>(null);

  private _investmentPictureColumnsDefinition$: Observable<
    KPI.ColumnDefinition[]
  >;
  private investmentPictureColumnsRefresh$ = new BehaviorSubject<void>(null);

  private _opexColumnsDefinition$: Observable<KPI.ColumnDefinition[]>;
  private opexColumnsRefresh$ = new BehaviorSubject<void>(null);

  private _mappings$: Observable<KPI.Mapping[]>;
  private mappingsRefresh$ = new BehaviorSubject<void>(null);

  private _typeMapping$: Observable<TypeMapping>;

  public selectAllRows$ = new BehaviorSubject<boolean>(null);
  public validateImport$ = new BehaviorSubject<any>(null);

  constructor(
    private api: APIService,
    private investmentExecutionsService: InvestmentExecutionsService,
    private investmentReasonsService: InvestmentReasonsService,
    private scope: ScopeService,
    private http: HttpClient,
    private roadmapService: RoadmapService,
  ) { }

  public get assetImportableInvestmentStatuses$(): Observable<
    KPI.Display<KPI.ImportableAssetStatus>[]
  > {
    if (!this._assetImportableInvestmentStatuses$) {
      this._assetImportableInvestmentStatuses$ = this.api
        .import_importable_asset_statuses()
        .pipe(shareReplay(1));
    }

    return this._assetImportableInvestmentStatuses$;
  }

  public get investmentImportableInvestmentStatuses$(): Observable<
    KPI.Display<KPI.ImportableInvestmentStatus>[]
  > {
    if (!this._investmentImportableInvestmentStatuses$) {
      this._investmentImportableInvestmentStatuses$ = this.api
        .import_importable_investment_statuses()
        .pipe(shareReplay(1));
    }

    return this._investmentImportableInvestmentStatuses$;
  }

  public get assetColumnsDefinition$(): Observable<KPI.ColumnDefinition[]> {
    if (!this._assetColumnsDefinition$) {
      this._assetColumnsDefinition$ = this.assetColumnsRefresh$.pipe(
        switchMap(() => this.api.import_asset_columns()),
        shareReplay(1),
      );
    }

    return this._assetColumnsDefinition$;
  }

  public refreshAssetColumnsDefinition(): void {
    this.assetColumnsRefresh$.next();
  }

  public get assetPictureColumnsDefinition$(): Observable<
    KPI.ColumnDefinition[]
  > {
    if (!this._assetPictureColumnsDefinition$) {
      this._assetPictureColumnsDefinition$ =
        this.assetPictureColumnsRefresh$.pipe(
          switchMap(() => this.api.import_asset_picture_columns()),
          shareReplay(1),
        );
    }

    return this._assetPictureColumnsDefinition$;
  }

  public refreshAssetPictureColumnsDefinition(): void {
    this.assetPictureColumnsRefresh$.next();
  }

  public get investmentColumnsDefinition$(): Observable<
    KPI.ColumnDefinition[]
  > {
    if (!this._investmentColumnsDefinition$) {
      this._investmentColumnsDefinition$ = this.investmentColumnsRefresh$.pipe(
        switchMap(() => this.api.import_investment_columns()),
        shareReplay(1),
      );
    }

    return this._investmentColumnsDefinition$;
  }

  public refreshInvestmentColumnsDefinition(): void {
    this.investmentColumnsRefresh$.next();
  }

  public get investmentPictureColumnsDefinition$(): Observable<
    KPI.ColumnDefinition[]
  > {
    if (!this._investmentPictureColumnsDefinition$) {
      this._investmentPictureColumnsDefinition$ =
        this.investmentPictureColumnsRefresh$.pipe(
          switchMap(() => this.api.import_investment_picture_columns()),
          shareReplay(1),
        );
    }

    return this._investmentPictureColumnsDefinition$;
  }

  public refreshInvestmentPictureColumnsDefinition(): void {
    this.investmentPictureColumnsRefresh$.next();
  }

  public get opexColumnsDefinition$(): Observable<KPI.ColumnDefinition[]> {
    if (!this._opexColumnsDefinition$) {
      this._opexColumnsDefinition$ = this.opexColumnsRefresh$.pipe(
        switchMap(() => this.api.import_opex_columns()),
        shareReplay(1),
      );
    }

    return this._opexColumnsDefinition$;
  }

  public refreshOpexColumnsDefinition(): void {
    this.opexColumnsRefresh$.next();
  }

  public get mappings$(): Observable<KPI.Mapping[]> {
    if (!this._mappings$) {
      this._mappings$ = this.mappingsRefresh$.pipe(
        switchMap(() => this.api.import_mappings()),
        shareReplay(1),
      );
    }

    return this._mappings$;
  }

  public refreshMapping(): void {
    this.mappingsRefresh$.next();
  }

  public createOrUpdateMapping(mapping: KPI.Mapping): Observable<KPI.Mapping> {
    if (mapping.id) {
      return this.api.import_mapping_patch(mapping.id, mapping);
    } else {
      return this.api.import_mapping_post(mapping);
    }
  }

  public get typeMapping$(): Observable<TypeMapping> {
    if (!this._typeMapping$) {
      this._typeMapping$ = combineLatest(
        this.scope.categories$.pipe(idMapToArray()), // TODO: filter children by parent
        this.scope.subCategories$.pipe(idMapToArray()), // TODO: filter children by parent
        this.scope.assetTypes$.pipe(idMapToArray()), // TODO: filter children by parent
        this.scope.zones$.pipe(idMapToArray()),
        this.scope.perimeters$.pipe(
          idMapToArray(),
          map((perim) => this.sortIdNameMixin(perim)),
        ),
        this.scope.monoperimeters$.pipe(
          idMapToArray(),
          map((perim) => this.sortIdNameMixin(perim)),
        ), // TODO: filter children by parent
        this.scope.ownershipTypes$.pipe(idMapToArray()),
        this.scope.investmentStatuses$.pipe(idMapToArray()),
        this.investmentReasonsService.investmentReasons$,
        this.investmentExecutionsService.investmentExecutions$,
        this.scope.investmentPriorities$.pipe(idMapToArray()),
        this.scope.investmentTypes$.pipe(idMapToArray()),
        this.scope.investmentBudgetOrigins$.pipe(idMapToArray()),
        this.scope.ratingReasons$.pipe(idMapToArray()),
        this.scope.assetPowerSources$.pipe(idMapToArray()),
        this.scope.investmentSponsors$.pipe(idMapToArray()),
        this.scope.investmentInvoicesRecipient$.pipe(idMapToArray()),
        this.scope.assetFloors$.pipe(idMapToArray()),
        this.scope.getInvestFurtherInfo$.pipe(idMapToArray()),
        this.scope.taxonomyCategories$.pipe(idMapToArray()),
        this.scope.heatTransferCoefficientU$.pipe(idMapToArray()),
        this.scope.sourceInfoHeatTransferCoefficientU$.pipe(idMapToArray()),
        this.scope.thermalResistanceCoefficientR$.pipe(idMapToArray()),
        this.scope.sourceInfoThermalResistanceCoefficientR$.pipe(idMapToArray()),
        this.scope.budgetScopes$.pipe(idMapToArray()),
        this.roadmapService.roadmapIndicators().pipe(first()),
      ).pipe(
        map(
          ([
            category,
            subcategory,
            assetType,
            zone,
            multiperimeter,
            monoperimeter,
            ownershipType,
            investmentStatus,
            investmentReason,
            mod,
            priority,
            investmentType,
            budgetOrigin,
            ratingReasons,
            assetPowerSources,
            investmentSponsors,
            investment_invoice_recipient,
            asset_floor,
            further_information,
            taxonomyCategory,
            heat_transfer_coefficient_u,
            source_info_heat_transfer_coefficient_u,
            thermal_resistance_coefficient_r,
            source_info_thermal_resistance_coefficient_r,
            budgetScope,
            roadmapIndicator,
          ]) => {
            return {
              category,
              subcategory,
              asset_type: assetType,
              zone,
              multiperimeter,
              monoperimeter,
              ownership_type: ownershipType,
              investmentStatus,
              investmentReason,
              mod,
              priority,
              investmentType,
              budgetOrigin,
              rating_reason: ratingReasons,
              power_source: assetPowerSources,
              investment_sponsor: investmentSponsors,
              investment_invoice_recipient,
              asset_floor,
              further_information,
              taxonomyCategory,
              heat_transfer_coefficient_u,
              source_info_heat_transfer_coefficient_u,
              thermal_resistance_coefficient_r,
              source_info_thermal_resistance_coefficient_r,
              budgetScope,
              roadmapIndicator,
            };
          },
        ),
        shareReplay(1),
      );
    }

    return this._typeMapping$;
  }

  public importAsset(importedData): Observable<KPI.Asset> {
    return this.api.import_import_asset(importedData);
  }

  public importAssetPicture(importedData): Observable<KPI.AssetPicture> {
    return this.api.import_import_asset_picture(importedData);
  }

  public importInvestment(importedData): Observable<KPI.Investment> {
    return this.api.import_import_investment(importedData);
  }

  public importInvestmentPicture(
    importedData,
  ): Observable<KPI.InvestmentPicture> {
    return this.api.import_import_investment_picture(importedData);
  }

  public importOpex(importedData): Observable<KPI.AssetOpex> {
    return this.api.import_import_opex(importedData);
  }

  public importOpexBulk(rows: any[]): Observable<any> {
    return this.api.import_import_opex_bulk(rows);
  }

  private sortIdNameMixin(typeMappings: IdNameMixin[]): IdNameMixin[] {
    return typeMappings.sort((type1, type2) => {
      const name1: string = type1.name.toLowerCase();
      const name2: string = type2.name.toLowerCase();

      return name1 === name2 ? 0 : name1 < name2 ? -1 : 1;
    });
  }

  public uploadConsumptions(file: File, perimeterId: number): Observable<Boolean> {
    return this.uploadExcel(`/energy/api/import-excel-file/?perimeter_id=${perimeterId}`, file);
  }

  private uploadExcel(endpoint: string, file: File): Observable<Boolean> {
    const formData = new FormData();

    formData.append("file", file);

    return this.http.post(
      `${environment.backendHost}${endpoint}`,
      formData
    ).pipe(
      map((_response) => {
        return true;
      }),
      catchError(_error => {
        return of(false);
      })
    );
  }
}
